import { useMemo } from 'react'
import {
  CUTS,
  INSTALLS,
  PAID,
  PREREQ,
  STATIONS,
  countState,
  type Install,
  type InstallStep,
  type Station,
  type StationState,
} from '@/domain/chain'
import { buildSkeleton, defaultState, runSimulation, type GaitKind } from '@/domain/hs1'
import { mjcfModel, rigFromModel, toUrdf } from '@/domain/rig'
import { handoffBundle, type BundleFile } from '@/domain/handoff/bundle'
import { motionExport } from '@/domain/handoff/motion'
import { modelId } from '@/domain/handoff/fingerprint'
import { Alert, Badge, Button, Card, Table, Td, Th } from '@/components/ui'
import { Metric, PageHead, num } from '@/sim/parts'
import { useSimulation } from '@/sim/SimulationProvider'

/**
 * Die Werkzeugkette — was steht und was nicht.
 *
 * WOZU DIE SEITE. Zwischen diesem Simulator und einem laufenden Roboter liegen
 * dreizehn Stationen. Welche davon gebaut sind, welche gekauft werden müssen
 * und welche noch niemand angefasst hat, stand bis hierher in einem Aufsatz —
 * und ein Aufsatz veraltet zwischen zwei Sätzen. Hier steht es als Liste, und
 * der Zustand jeder Station steht neben ihr.
 *
 * DIE ZAHLEN OBEN SIND GEZÄHLT UND NICHT GESCHRIEBEN: wer eine Station baut,
 * ändert eine Zeile in `chain.ts`, und die Übersicht zieht nach.
 */

const TONE: Record<StationState, 'success' | 'warning' | 'neutral' | 'danger'> = {
  gebaut: 'success',
  platzhalter: 'warning',
  fremd: 'neutral',
  offen: 'danger',
}

/**
 * Welche Bewegungen mit ins Bündel gehen.
 *
 * `stand` fehlt bewusst: eine Bewegung ohne Bewegung ist als Vorbild nichts
 * wert, und ein Regler, der darauf trainiert, lernt stillzustehen.
 */
const WEITERE: readonly GaitKind[] = ['walk', 'run', 'sprint', 'carry', 'hop', 'pick']

const LABEL: Record<StationState, string> = {
  gebaut: 'gebaut',
  platzhalter: 'Platzhalter',
  fremd: 'zu beschaffen',
  offen: 'offen',
}

export function ChainPage() {
  const schnitte = new Map(CUTS.map((c) => [c.after, c.what]))
  const { model, state, simulation, robot } = useSimulation()

  /*
    DAS BÜNDEL HÄNGT AM AKTUELLEN STAND und nicht an einem gespeicherten: was
    hier heruntergeladen wird, ist das, was die Seiten daneben zeigen.
  */
  const { files, motion, gaps } = useMemo(() => {
    const skeleton = buildSkeleton(model)
    const rig = rigFromModel(model, state.off)
    const off = new Set(Object.keys(state.off))
    /* Erst die Kennung, dann die Dateien: sie steht im Kopf jeder von ihnen. */
    const kennung = modelId(model, robot.id)
    const mjcf = mjcfModel(model, skeleton, rig, { name: robot.id, off, modelId: kennung })
    const bewegung = motionExport(simulation, rig, mjcf.joints, { model: robot.id })
    /*
      DIE ÜBRIGEN GANGARTEN KOMMEN MIT. Ein Regler, der nur das Gehen als
      Vorbild bekommt, lernt das Gehen und fällt beim ersten Laufschritt um.
      Gerechnet wird dafür je Gangart einmal — der Zwischenspeicher vor dem
      Kern trägt das, und die Seite wird nur beim ersten Öffnen langsamer.
    */
    const weitere = WEITERE.filter((g) => g !== state.gait).map((gait) =>
      motionExport(runSimulation(model, defaultState(gait)), rig, mjcf.joints, {
        model: robot.id,
      }),
    )
    return {
      motion: bewegung,
      gaps: mjcf.gaps,
      files: handoffBundle({
        name: robot.id,
        mjcf: mjcf.xml,
        urdf: toUrdf(model, skeleton, rig, { name: robot.id, off, modelId: kennung }),
        motion: bewegung,
        more: weitere,
        gaps: mjcf.gaps,
      }),
    }
  }, [model, state, simulation, robot.id])

  return (
    <div className="stack">
      <PageHead title="Werkzeugkette" />

      <div className="metrics">
        <Metric
          label="Stationen"
          value={String(STATIONS.length)}
          note="vom Master bis zum Bordrechner"
        />
        <Metric label="Gebaut" value={String(countState('gebaut'))} note="läuft und ist geprüft" />
        <Metric
          label="Zu beschaffen"
          value={String(countState('fremd'))}
          note="fertige Software, fremde Hardware"
        />
        <Metric
          label="Kostet Lizenzgeld"
          value={String(PAID.length)}
          note={`von ${STATIONS.length} — der Rest ist frei`}
        />
      </div>

      <Alert tone="info" title="Was sich seit der ersten Fassung verschoben hat">
        <p>
          <b>Newton ist seit März 2026 fertig und liest MJCF unmittelbar.</b> Damit ist die Datei,
          die aus diesem Simulator fällt, nicht mehr das Eingangsformat eines Nebenwerkzeugs,
          sondern das der Trainingsstrecke selbst. Der Aufwand für den Erzeuger zahlt sich an zwei
          Stellen gleichzeitig aus.
        </p>
        <p>
          <b>MuJoCo ist keine zweite Physik mehr</b> — der Standardlöser von Newton <i>ist</i>{' '}
          MuJoCo. Station 6 bleibt trotzdem, mit einer besseren Begründung: sie ist die kurze
          Schleife und die zweite <i>Richtung</i>. Die Gegenprobe findet nicht zwischen zwei
          Physiken statt, sondern zwischen unserer Gangrechnung und der Vorwärtsrechnung.
        </p>
        <p>
          Damit ist auch die Parallelkinematik kein Sonderfall mehr, der eine eigene Station
          rechtfertigt: Newton hat einen eigenen Löser für geschlossene Ketten.
        </p>
      </Alert>

      <Card
        title="Übergabe an die Trainingsstrecke"
        actions={
          <Button variant="primary" onClick={() => files.forEach(download)}>
            Alle {files.length} Dateien
          </Button>
        }
      >
        <p className="page-lead">
          Die Stationen 3 bis 5 laufen nicht im Browser — Isaac Sim, Newton und Isaac Lab sind
          native Programme auf einer Grafikkarte. Was diese Anwendung dafür tun kann, ist alles
          bereitzulegen, was sie brauchen, erzeugt aus dem aktuellen Modellstand.
        </p>
        <div className="metrics">
          <Metric
            label="Modellstand"
            value={motion.modelId}
            note="steht in jeder Datei des Bündels"
          />
          <Metric
            label="Modell"
            value={`${num(motion.mass, 2)} kg`}
            note={`${motion.joints.length} Achsen · ${motion.joints.filter((j) => !j.passive).length} angetrieben`}
          />
          <Metric
            label="Vorbild"
            value={`${num(motion.speed, 2)} m/s`}
            note={`${motion.samples} Stützstellen je Doppelschritt`}
          />
          <Metric
            label="Ohne Vorbild"
            value={String(motion.joints.filter((j) => !j.recorded).length)}
            note="Achsen, die der Referenzgang nicht führt"
            warn={motion.joints.some((j) => !j.recorded)}
          />
        </div>
        <Table>
          <thead>
            <tr>
              <Th>Datei</Th>
              <Th>Wofür</Th>
              <Th align="right">Grösse</Th>
              <Th align="right">Laden</Th>
            </tr>
          </thead>
          <tbody>
            {files.map((f) => (
              <tr key={f.name}>
                <Td>{f.name}</Td>
                <Td>{ZWECK[f.name.replace(robot.id, '<name>')] ?? ''}</Td>
                <Td align="right" numeric>
                  {(f.content.length / 1024).toFixed(0)} kB
                </Td>
                <Td align="right">
                  <Button size="sm" variant="ghost" onClick={() => download(f)}>
                    Herunterladen
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        {gaps.length > 0 && (
          <Alert tone="warning" title="Was mitgeht und was fehlt">
            {gaps.map((g) => (
              <p key={g.node}>
                <b>
                  {g.node} ({g.axes.join(', ')})
                </b>{' '}
                — {g.reason}. Steht auch in der README des Bündels.
              </p>
            ))}
          </Alert>
        )}
        <p className="metric__note">
          Der Trainingscode ist bewusst <b>nicht</b> im Bündel. Er liegt im Repository unter{' '}
          <code>handoff/</code> und wird dort gepflegt: ein Trainingsskript, das jemand aus einem
          Download heraus ändert, ist ab dann von der Versionsgeschichte abgeschnitten.
        </p>
      </Card>

      <Card flush title="Die Kette">
        <Table>
          <thead>
            <tr>
              <Th>#</Th>
              <Th>Station</Th>
              <Th>Zweck — und was sie nicht tut</Th>
              <Th>Lizenz</Th>
              <Th align="right">Kosten</Th>
              <Th>Stand</Th>
            </tr>
          </thead>
          <tbody>
            {STATIONS.map((s) => (
              <StationRows key={s.no} station={s} cut={schnitte.get(s.no)} />
            ))}
          </tbody>
        </Table>
      </Card>

      <Card title="Einrichtung — in dieser Reihenfolge">
        <p className="page-lead">
          Die Reihenfolge ist nicht beliebig. Erst das Modell ansehen, wo es nichts kostet; dann
          das, was ohne Grafikkarte geht; dann die Trainingsstrecke. Wer mit Station 5 anfängt,
          sucht einen Modellfehler in einem Trainingslauf.
        </p>
        <div className="chain__prereq">
          <h3 className="chain__h">Bevor irgendetwas installiert wird</h3>
          {PREREQ.map((s) => (
            <Step key={s.cmd} step={s} />
          ))}
        </div>
        {INSTALLS.map((i) => (
          <InstallBlock key={i.station} install={i} />
        ))}
        <Alert tone="info" title="Warum an jedem Block steht, woher er kommt">
          Ein abgeschriebener Befehl aus der Dokumentation und einer aus der Erinnerung sehen
          gleich aus und sind es nicht. Was hier <b>dokumentiert</b> heisst, ist gegen die
          Anleitung des Herstellers geschrieben; was <b>abgeleitet</b> heisst, ist unsere eigene
          Folgerung und noch nicht auf einer Maschine gelaufen. Wer das nicht trennt, sucht den
          Fehler später an der falschen Stelle.
        </Alert>
      </Card>

      <Card title="Was tatsächlich Geld kostet">
        <p className="page-lead">
          Die Software der Lernstrecke ist frei. Bezahlt werden drei Dinge: das CAD, die Rechner
          und die Zeit für PLM, Beschreibung und Antriebsmodell.
        </p>
        <p className="metric__note">
          Das Unternehmens-Abonnement wird nur fällig, wenn die Simulationsumgebung selbst an Dritte
          geht oder als Dienst betrieben wird — rund 5.800 €/Jahr je Grafikkarte. Reine Ergebnisse
          (Datensätze, Regler, Berichte, eigener Code) lösen es nicht aus; für interne Entwicklung
          mit eigener Geometrie greift es nicht. Richtwerte für die Planung, kein Angebot und keine
          Rechtsberatung.
        </p>
      </Card>
    </div>
  )
}

/**
 * Ein Block der Einrichtung — eine Station, ihre Begründung und ihre Schritte.
 *
 * DIE BEGRÜNDUNG STEHT OBEN und nicht als Fussnote. Eine Anleitung, die nur
 * sagt WAS zu tippen ist, wird abgetippt; eine, die sagt WOFÜR, wird verstanden
 * — und nur die zweite hilft, wenn ein Befehl nicht das tut, was danebensteht.
 */
function InstallBlock({ install }: { install: Install }) {
  const station = STATIONS.find((s) => s.no === install.station)
  return (
    <section className="chain__install">
      <h3 className="chain__h">
        {install.title}
        <Badge tone={install.confidence === 'dokumentiert' ? 'success' : 'warning'}>
          {install.confidence}
        </Badge>
        {station && <span className="chain__station">Station {station.no}</span>}
      </h3>
      <p className="page-lead">{install.why}</p>
      {install.steps.map((s) => (
        <Step key={s.cmd} step={s} />
      ))}
      <p className="metric__note">Quelle: {install.source}</p>
    </section>
  )
}

/**
 * Ein Schritt: was, der Befehl, und woran man sieht, dass es geklappt hat.
 *
 * `verify` IST DER TEIL, DEN ANLEITUNGEN WEGLASSEN. Ohne ihn merkt man einen
 * halb gelungenen Schritt erst drei Schritte später, an einem Fehler, der wie
 * etwas anderes aussieht.
 */
function Step({ step }: { step: InstallStep }) {
  return (
    <div className="chain__step">
      <p className="chain__what">{step.what}</p>
      <pre className="chain__cmd">{step.cmd}</pre>
      {step.verify && (
        <p className="chain__verify">
          <b>Geschafft, wenn:</b> {step.verify}
        </p>
      )}
      {step.note && <p className="metric__note">{step.note}</p>}
    </div>
  )
}

/** Wofür jede Datei des Bündels da ist. */
const ZWECK: Record<string, string> = {
  '<name>.xml': 'MJCF — Newton und MuJoCo lesen es unmittelbar',
  '<name>.urdf': 'URDF — für den Weg über Isaac Sim, solange Isaac Lab auf der bisherigen Physik läuft',
  'motion.json': 'Referenzbewegung — das Vorbild, gegen das belohnt wird',
  'modality.json': 'Kanalbeschreibung für GR00T: welcher Abschnitt welche Achse ist',
  'README.md': 'Was womit zu tun ist, mit den Befehlen',
}

/**
 * Eine Datei anbieten.
 *
 * Über ein Objekt-URL und nicht über einen Server: die Datei entsteht im
 * Browser aus Zahlen, die dort schon liegen. Ein Rundgang über eine
 * Serverfunktion wäre ein zweiter Ort, an dem dasselbe Bündel entstünde.
 */
function download(file: BundleFile): void {
  const url = URL.createObjectURL(new Blob([file.content], { type: file.type }))
  const link = document.createElement('a')
  link.href = url
  link.download = file.name
  link.click()
  URL.revokeObjectURL(url)
}

/**
 * Eine Station, und darunter der Einschnitt, wenn hinter ihr einer liegt.
 *
 * Der Einschnitt steht als eigene Zeile im Fluss der Tabelle und nicht als
 * Fussnote daneben: er gehört zwischen zwei Stationen, weil er genau das
 * beschreibt — was sich beim Übergang ändert.
 */
function StationRows({ station, cut }: { station: Station; cut?: string }) {
  return (
    <>
      <tr>
        <Td numeric>{station.no}</Td>
        <Td>{station.name}</Td>
        <Td>
          {station.purpose}
          <br />
          <span className="cell-muted">{station.notThis}</span>
          {station.where && (
            <>
              <br />
              <span className="cell-muted">In dieser Anwendung: {station.where}.</span>
            </>
          )}
          {station.next && (
            <>
              <br />
              <span className="cell-muted">Nächster Schritt: {station.next}</span>
            </>
          )}
        </Td>
        <Td>{station.licence}</Td>
        <Td align="right" numeric>
          {station.cost ?? <span className="cell-muted">—</span>}
        </Td>
        <Td>
          <Badge tone={TONE[station.state]}>{LABEL[station.state]}</Badge>
        </Td>
      </tr>
      {cut && (
        <tr>
          <Td />
          <Td colSpan={5}>
            <b>Einschnitt.</b> <span className="cell-muted">{cut}</span>
          </Td>
        </tr>
      )}
    </>
  )
}
